import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import dayjs from 'dayjs';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { BudgetModel } from '../../../../../../Generated/Commodity-Management-Api';

interface BudgetDateRange {
    startMonth: number;
    startYear: number;
    endMonth: number;
    endYear: number;
}

interface BudgetContextType {
    budgetDateRange: BudgetDateRange;
    isLoading: boolean;
    refreshBudgetRange: () => Promise<void>;
}

const defaultBudgetDateRange: BudgetDateRange = {
    startMonth: 1,
    startYear: dayjs().year() - 1,
    endMonth: 12,
    endYear: dayjs().year(),
};

const BudgetContext = createContext<BudgetContextType>({
    budgetDateRange: defaultBudgetDateRange,
    isLoading: false,
    refreshBudgetRange: async () => {},
});

export const useBudgetContext = () => useContext(BudgetContext);

interface BudgetProviderProps {
    children: ReactNode;
}

export const BudgetProvider: React.FC<BudgetProviderProps> = ({ children }) => {
    const [budgetDateRange, setBudgetDateRange] = useState<BudgetDateRange>(defaultBudgetDateRange);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const refreshBudgetRange = async () => {
        setIsLoading(true);
        try {
            const response = await CommodityManagementApi.listBudgets(
                null,
                undefined, // quoteDate
                undefined, // budgetStartMonthYear
                undefined, // budgetStartMonthMonth
                undefined, // budgetStartMonthLabel
                undefined, // budgetEndMonthYear
                undefined, // budgetEndMonthMonth
                undefined, // budgetEndMonthLabel
                undefined, // fiscalYearsFilter
                undefined, // commoditiesFilter
                undefined, // locationsFilter
                undefined, // counterpartiesFilter
                undefined, // budgetRoundsFilter
                undefined, // includeExpirationDates
                false,     // includePositions
                false,     // includeMarketPrices
                false      // includeBudgetRounds
            );

            const budgets = response.data.rows || [];
            if (budgets.length > 0) {
                // Find max budget date
                let maxYear = 0;
                let maxMonth = 0;

                budgets.forEach((budget: BudgetModel) => {
                    if (budget.budgetDate && budget.budgetDate.year !== undefined && budget.budgetDate.month !== undefined) {
                        const year = budget.budgetDate.year;
                        const month = budget.budgetDate.month;
                        if (year > maxYear || (year === maxYear && month > maxMonth)) {
                            maxYear = year;
                            maxMonth = month;
                        }
                    }
                });

                if (maxYear > 0 && maxMonth > 0) {
                    setBudgetDateRange({
                        startMonth: 1,
                        startYear: dayjs().year() - 1,
                        endMonth: maxMonth,
                        endYear: maxYear,
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch budgets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refreshBudgetRange();
    }, []);

    return (
        <BudgetContext.Provider value={{ budgetDateRange, isLoading, refreshBudgetRange }}>
            {children}
        </BudgetContext.Provider>
    );
};

export default BudgetContext;
